import type { SourceDocumentUIPart } from "ai";
import { Eye, FileText } from "lucide-react";

/**
 * Chip list for files attached as context to an upcoming chat message.
 *
 * Renders as an inline `Attachments` list (the same ai-elements primitive
 * the image-upload affordance uses), so the composer reads as a single
 * unified "what's attached to this message" surface. Two flavors:
 *
 *   - source: "open"    — auto-pinned to the file the user is currently
 *                         viewing in the side pane.
 *   - source: "mention" — added by an @-mention.
 *
 * Both are removable when the list is editable (`onRemove` is set).
 *
 * Chips flow horizontally and wrap to additional rows when they don't
 * fit. Each chip shows just the file basename (full path in `title=` for
 * the tooltip) and is capped at `max-w-[14rem]` so a single very long
 * path can't push the composer into horizontal scroll.
 */
import {
  Attachment,
  AttachmentInfo,
  AttachmentRemove,
  Attachments,
} from "../ai-elements/attachments";
import type { ContextRef } from "./types";

export type { ContextRef } from "./types";

interface ContextChipsProps {
  refs: ContextRef[];
  /**
   * Called when a chip's remove button is clicked. Omit to render the chip
   * list as read-only (e.g. inside a sent message bubble), in which case no
   * chip shows a remove button.
   */
  onRemove?: (ref: ContextRef) => void;
}

function basename(path: string): string {
  const i = path.lastIndexOf("/");
  return i === -1 ? path : path.slice(i + 1);
}

export function ContextChips({ refs, onRemove }: ContextChipsProps) {
  if (refs.length === 0) return null;
  return (
    <Attachments variant="inline" className="min-w-0 max-w-full gap-1">
      {refs.map((ref) => {
        const data: SourceDocumentUIPart & { id: string } = {
          id: `ctx:${ref.path}`,
          type: "source-document",
          sourceId: ref.path,
          mediaType: "text/markdown",
          title: basename(ref.path),
        };
        const isOpen = ref.source === "open";
        const Icon = isOpen ? Eye : FileText;
        return (
          <Attachment
            key={ref.path}
            data={data}
            onRemove={onRemove ? () => onRemove(ref) : undefined}
            title={isOpen ? `Currently viewing ${ref.path}` : ref.path}
            className="h-6 max-w-[14rem] min-w-0 cursor-default border-0 bg-muted/50 px-1.5 font-normal text-xs hover:bg-muted"
          >
            <Icon className="size-3 shrink-0" />
            <AttachmentInfo />
            {onRemove ? <AttachmentRemove /> : null}
          </Attachment>
        );
      })}
    </Attachments>
  );
}
